// @rpg-unit/impact-feedback: impact feedback (tier 4, a feature; composition framework, decision 0077), cut from spike
// 002's impact director (`src/feedback/index.ts`, gate B6). One standard `impact` event (@rpg/contracts ImpactEvent:
// a blow, a tool on a rock, a harvest's pop) fans out through @rpg/vfx's ImpactRouter, by its kind as a cue table
// says (default: @rpg-unit/impact-cue-presets), to the channels registered here:
//   hitStop    clock.hitStop(seconds) on the runtime's clock, requested in the sim step the impact happens in
//   flash      the struck body's flash (`track` gives a body a shell), decaying on real time, capped per second
//   shake      camera trauma × severity on @rpg/camera's shake, falling off with distance from the camera's pivot
//   kick       the camera pushed along the blow, perSeverity × severity m, decaying as e^(−kickRate t)
//   particles  @rpg/vfx bursts at the point, aimed along the blow, by emitter id
//   sound      the game's sound callback gets the cue name and the impact (it is also recorded for checks)
//   rumble     no sink unless the game gives one: the router counts it as sinkless
// Two more inputs a game wires from its own events (this feature imports no combat): `moveWindow` starts a weapon
// trail when a move's active window opens and stops it after; `telegraph` draws a move's danger zone on the ground,
// filling over its lead time.
//
// Clocks: hit-stop is requested in the sim step (deterministic). Everything else is view: flashes and the camera's
// shake and kick decay on real time (a hit-stop doesn't hold them), particles and trails run on the scaled frame
// time (they freeze with the blade), telegraphs fill on sim time (the fill lands with the blow). The player's shake
// slider and reduce motion (read every frame) scale the camera's shake and kick; at 0 or with reduce motion on there
// is no camera offset at all.
//
// Planted defect (`IMPACT_FEEDBACK_FAULTS.channelUnregistered`, beside what it breaks): the flash channel is never
// registered; a per-kind cue count must go red and the router reports it missing.
import type * as THREE from "three";
import type { CameraRig } from "@rpg/camera";
import type { ImpactEvent, Vec3Tuple } from "@rpg/contracts";
import type { Runtime } from "@rpg/runtime";
import { createFlashShell, createVfx, Flashes, ImpactRouter, Telegraphs, Trails, type CueTable, type EmitterSpec, type FlashShell, type TelegraphShape, type TelegraphStyle, type VfxSystem, type WeaponSample } from "@rpg/vfx";
import { cueTable, emitterSpecs, feedbackFeel, type FeedbackPreset } from "@rpg-unit/impact-cue-presets";

/** This unit's version (the manifest's and package.json's). */
export const IMPACT_FEEDBACK_VERSION = "0.1.0" as const;

/** The channels the feature registers a sink for (rumble only when the game gives one). */
export const CHANNELS = ["hitStop", "flash", "shake", "kick", "particles", "sound"] as const;
/** Channels a cue table may name with no sink, on purpose (rumble, until a game has haptics). */
export const SINKLESS = ["rumble"] as const;

/** The planted defect's switch (set it, then `applyImpactFeedbackFaults()` re-registers every mounted feature). */
export const IMPACT_FEEDBACK_FAULTS = { channelUnregistered: false };
/** The channel the planted defect leaves unregistered. */
export const UNREGISTERED_CHANNEL = "flash";
const mounted = new Set<() => void>();
/** Re-register every mounted feature's channels under the switches now. */
export function applyImpactFeedbackFaults(): void {
  for (const apply of mounted) apply();
}

/** What the feature fans out from: the cue table, the bursts it names, the telegraph styles and the feel. */
export interface ImpactFeedbackValues {
  /** kind → channel → cue (@rpg/vfx's router table). */
  cues: CueTable;
  /** Particle bursts by id. */
  emitters: Record<string, EmitterSpec>;
  /** Telegraph styles by id; `normal` and `unblockable` are the fallbacks a telegraph without its own style takes. */
  styles: Record<string, TelegraphStyle>;
  /** The shake's shape, trails, the flash colour, silent outcomes. */
  feel: FeedbackPreset;
}

/** The impact cue presets as values (the action set), with the game's telegraph styles. */
export function presetValues(styles: Record<string, TelegraphStyle> = {}): ImpactFeedbackValues {
  return { cues: cueTable(), emitters: emitterSpecs(), styles, feel: feedbackFeel() };
}

/** Where impacts come from: anything with the standard event bus's `on` for `impact` (a kernel game's events). */
export interface ImpactSource {
  on(event: "impact", fn: (e: ImpactEvent) => void): () => void;
}

/** What the feature needs from the scene it serves. */
export interface ImpactFeedbackDeps {
  runtime: Runtime;
  /** The impacts, emitted in the sim step they happen. */
  events: ImpactSource;
  /** Where the particles, telegraphs and trails are drawn. */
  scene: THREE.Object3D;
  values: ImpactFeedbackValues;
  /** The camera's rig (its shake and kick); null for a scene without one. */
  rig: CameraRig | null;
  /** Where a weapon's base and tip are now (world space), for trails. */
  weaponOf: WeaponSample;
  /** The ground's height (telegraphs drape on it). */
  heightAt: (x: number, z: number) => number;
  /** The player's settings now: the shake slider (0..1) and reduce motion. */
  motion: () => { shake: number; reduceMotion: boolean };
  /** The sound channel's sink: the cue's name and the impact (null or absent: recorded only). */
  sound?: ((cue: string, e: ImpactEvent) => void) | null;
  /** Flashes allowed to start per real second (an accessibility setting); read each frame. Default: no cap. */
  flashCap?: () => number;
}

/** A move's phase change, as a game's combat reports it. */
export interface MoveWindow {
  entity: number;
  phase: string;
  /** The weapon swung (a trail needs one). */
  weapon?: string | null;
}

/** A telegraphed move's wind-up, as a game's combat reports it. */
export interface TelegraphRequest {
  attacker: number;
  shape: TelegraphShape;
  origin: readonly [number, number, number];
  facing: number;
  /** Seconds until the blow lands. */
  lead: number;
  /** The move's own style id (else `unblockable` or `normal`). */
  style?: string | null;
  unblockable?: boolean;
}

/** The mounted feature. */
export interface ImpactFeedback {
  router: ImpactRouter<ImpactEvent>;
  flashes: Flashes;
  telegraphs: Telegraphs;
  trails: Trails;
  vfx: VfxSystem;
  /** A body that can flash (its meshes get a shell). The game calls it when a body spawns. */
  track(entity: number, root: THREE.Object3D): void;
  untrack(entity: number): void;
  /** A move's window: an active phase with a weapon starts its trail; recovery or end stops it. */
  moveWindow(m: MoveWindow): void;
  /** A telegraph: the attacker's earlier one is cancelled and this one starts filling over its lead. */
  telegraph(t: TelegraphRequest): void;
  /** Sound cues taken. */
  sounds(): string[];
  /** Compile every program the feature draws with (a shell, a telegraph, a trail, both particle blends) and clear
   *  them again: call before a warm-up render, so nothing compiles in play. */
  warm(origin: Vec3Tuple): () => void;
  /** A summary for a game's handle and checks. */
  report(): ImpactFeedbackReport;
  dispose(): void;
}

/** What `report()` gives. */
export interface ImpactFeedbackReport {
  counts: ReturnType<ImpactRouter<ImpactEvent>["counts"]>;
  channels: string[];
  missing: string[];
  flashes: Record<string, number>;
  flashesStarted: number;
  flashesCapped: number;
  flashPeakRate: number;
  telegraphs: ReturnType<Telegraphs["state"]>;
  telegraphsStarted: Record<string, number>;
  trails: ReturnType<Trails["state"]>;
  trailsStarted: number;
  vfx: ReturnType<VfxSystem["counts"]>;
  sounds: string[];
  camera: { offset: number; roll: number; fov: number; level: { trauma: number; kick: number; punch: number }; counts: Record<string, number> } | null;
}

/** Mount the feature on a scene. */
export function mountImpactFeedback(d: ImpactFeedbackDeps): ImpactFeedback {
  const v = d.values;
  const { runtime } = d;
  if (d.rig) d.rig.shake.settings = v.feel.shake;
  const quiet = console.log;
  console.log = () => undefined; // three.quarks greets the console once
  const vfx = createVfx();
  console.log = quiet;
  d.scene.add(vfx.root);
  const telegraphs = new Telegraphs({ heightAt: d.heightAt, styles: v.styles });
  d.scene.add(telegraphs.root);
  const trails = new Trails(v.feel.trails);
  d.scene.add(trails.root);
  const flashes = new Flashes();
  const shells = new Map<number, FlashShell>();
  const sounds: string[] = [];

  const router = new ImpactRouter<ImpactEvent>({ cues: v.cues, sinkless: SINKLESS });
  const num = (x: unknown): number => (typeof x === "number" ? x : 0);
  const sinks: Record<(typeof CHANNELS)[number], (cue: Readonly<Record<string, unknown>>, e: ImpactEvent) => void> = {
    hitStop: (cue) => runtime.clock.hitStop(num(cue.seconds)),
    flash: (cue, e) => {
      if (e.target !== null) flashes.hit(e.target, num(cue.peak), num(cue.life));
    },
    shake: (cue, e) => {
      if (!d.rig) return;
      const pivot = d.rig.frame().pivot;
      d.rig.shake.addTrauma(num(cue.trauma) * e.severity, { x: e.point[0], y: e.point[1], z: e.point[2] }, pivot);
    },
    kick: (cue, e) => d.rig?.shake.kick({ x: e.dir[0], y: e.dir[1], z: e.dir[2] }, num(cue.perSeverity) * e.severity),
    particles: (cue, e) => {
      for (const id of [cue.emitter, cue.extra]) {
        if (typeof id !== "string") continue;
        const spec = v.emitters[id];
        if (!spec) throw new Error(`no emitter "${id}"`);
        vfx.add(spec, { position: e.point, direction: e.dir, autoRemove: true });
      }
    },
    sound: (cue, e) => {
      sounds.push(String(cue.cue));
      d.sound?.(String(cue.cue), e);
    },
  };
  // every channel registered, except the planted defect's
  const register = (): void => {
    for (const ch of CHANNELS) {
      if (IMPACT_FEEDBACK_FAULTS.channelUnregistered && ch === UNREGISTERED_CHANNEL) router.register(ch, () => undefined)();
      else router.register(ch, sinks[ch]);
    }
  };
  register();
  mounted.add(register);

  const silent = new Set<string>(v.feel.silentOutcomes);
  const offs = [
    d.events.on("impact", (e) => {
      if (e.outcome !== undefined && silent.has(e.outcome)) return;
      router.fire(e);
    }),
    runtime.on(
      "late",
      (c) => {
        // the player's motion settings, every frame (a settings screen changes them live)
        if (d.rig) {
          const m = d.motion();
          d.rig.shake.strength = m.shake;
          d.rig.shake.reduceMotion = m.reduceMotion;
        }
        flashes.cap = d.flashCap?.() ?? Infinity;
        flashes.update(c.realDt);
        for (const [id, s] of shells) s.set(flashes.value(id));
        trails.update(c.dt, d.weaponOf);
        vfx.update(c.dt);
        telegraphs.update(c.time - runtime.clock.dt * (1 - c.alpha));
      },
      // before a camera rig (order -10) reads the shake settings
      { order: -30, name: "feedback: view" },
    ),
  ];

  const report = (): ImpactFeedbackReport => {
    const shake = d.rig?.frame().shake;
    return {
      counts: router.counts(),
      channels: router.channels(),
      missing: router.missing(),
      flashes: Object.fromEntries([...shells].map(([id, s]) => [id, s.intensity])),
      flashesStarted: flashes.started,
      flashesCapped: flashes.capped,
      flashPeakRate: flashes.peakRate,
      telegraphs: telegraphs.state(),
      telegraphsStarted: { ...telegraphs.started },
      trails: trails.state(),
      trailsStarted: trails.started,
      vfx: vfx.counts(),
      sounds: [...sounds],
      camera: shake ? { offset: Math.hypot(shake.offset.x, shake.offset.y, shake.offset.z), roll: shake.roll, fov: shake.fov, level: d.rig!.shake.level(), counts: { ...d.rig!.shake.counts } } : null,
    };
  };

  return {
    router,
    flashes,
    telegraphs,
    trails,
    vfx,
    track(entity, root) {
      shells.get(entity)?.dispose();
      shells.set(entity, createFlashShell(root, { colour: v.feel.flashColour }));
    },
    untrack(entity) {
      shells.get(entity)?.dispose();
      shells.delete(entity);
    },
    moveWindow(m) {
      if (m.phase === "active" && m.weapon) trails.start(m.entity);
      else if (m.phase === "recovery" || m.phase === "end") trails.stop(m.entity);
    },
    telegraph(t) {
      telegraphs.cancelOwner(t.attacker);
      // the move's own style, else by whether it can be blocked
      const style = t.style && v.styles[t.style] ? t.style : t.unblockable ? "unblockable" : "normal";
      telegraphs.start({ shape: t.shape, origin: [...t.origin], facing: t.facing, lead: t.lead, style, owner: t.attacker }, runtime.clock.time);
    },
    sounds: () => [...sounds],
    warm(origin) {
      const clearTelegraphs = telegraphs.warm(origin, "normal");
      const anyShell = shells.values().next().value as FlashShell | undefined;
      anyShell?.set(1);
      trails.start(-1);
      trails.started--; // a warm-up isn't a trail shown
      const fake: WeaponSample = (o) => (o === -1 ? { base: [origin[0], origin[1] + 1, origin[2]], tip: [origin[0] + 1, origin[1] + 1.5, origin[2]] } : null);
      trails.update(0, fake);
      trails.update(0.01, (o) => (o === -1 ? { base: [origin[0], origin[1] + 1.2, origin[2]], tip: [origin[0] + 1, origin[1] + 1.8, origin[2]] } : null));
      // one particle system per blend mode, so their batches (and programs) exist from boot
      const bursts = (["additive", "normal"] as const).map((blending) =>
        vfx.add({ shape: { type: "point" }, rate: 0, bursts: [{ time: 0, count: 2 }], life: 0.05, speed: 0, size: 0.01, color: [1, 1, 1, 0], looping: false, duration: 0.05, blending }, { position: origin }),
      );
      vfx.update(0.001);
      return () => {
        clearTelegraphs();
        anyShell?.set(0);
        trails.stop(-1);
        trails.update(1, fake);
        for (const b of bursts) vfx.remove(b);
      };
    },
    report,
    dispose() {
      mounted.delete(register);
      offs.forEach((o) => o());
      for (const s of shells.values()) s.dispose();
      telegraphs.dispose();
      trails.dispose();
      vfx.dispose();
    },
  };
}
